/**
 * D1 · My takes — drafts stay private until submitted (submit/delete here).
 * D9 · History of submitted & reviewed takes, archived collapsed.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { api } from '../../lib/api.ts';
import { t, exName as exLabel, exDesc as exDescription } from '../../lib/i18n.ts';
import { Button, Card, EmptyState, ErrorText, Spinner, Tag, fmtDateTime } from '../../lib/ui.tsx';

interface TakeRow {
  id: string;
  exerciseId: string;
  status: 'draft' | 'submitted' | 'reviewed' | 'archived';
  accuracyScore: number | null;
  reaction: string | null;
  createdAt: string;
  submittedAt: string | null;
}

const REACTION_EMOJI: Record<string, string> = {
  great: '✓🎉',
  nice_progress: '📈',
  keep_going: '💪',
};

export default function TakesPage() {
  const qc = useQueryClient();
  const [error, setError] = useState<unknown>(null);
  const [showArchived, setShowArchived] = useState(false);

  const { data: takes, isLoading } = useQuery({
    queryKey: ['takes'],
    queryFn: () => api<TakeRow[]>('/api/takes'),
  });
  const { data: exercises } = useQuery({
    queryKey: ['exercises'],
    queryFn: () => api<{ id: string; name: string }[]>('/api/exercises'),
  });

  const exerciseName = (id: string) => exLabel(exercises?.find((e) => e.id === id)) ?? 'Exercise';

  const invalidate = () => void qc.invalidateQueries({ queryKey: ['takes'] });

  const submit = useMutation({
    mutationFn: (id: string) => api(`/api/takes/${id}/submit`, { method: 'POST', body: {} }),
    onSuccess: invalidate,
    onError: setError,
  });
  const remove = useMutation({
    mutationFn: (id: string) => api(`/api/takes/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
    onError: setError,
  });

  if (isLoading) return <Spinner />;
  if (!takes) return null;

  const drafts = takes.filter((tk) => tk.status === 'draft');
  const awaiting = takes.filter((tk) => tk.status === 'submitted');
  const reviewed = takes.filter((tk) => tk.status === 'reviewed');
  const archived = takes.filter((tk) => tk.status === 'archived');

  return (
    <div className="space-y-4">
      <h1 className="text-lg font-bold">{t('takes.title', 'My takes')}</h1>

      {takes.length === 0 && (
        <EmptyState
          title={t('takes.empty', 'No takes yet.')}
          hint={t('takes.emptyHint', 'Record a take while practicing — it stays private until you submit it.')}
        />
      )}

      {drafts.length > 0 && (
        <section className="space-y-2">
          <SectionHeader label={t('takes.drafts', 'Drafts')} hint={t('takes.draftsHint', 'only you can see these')} />
          {drafts.map((take) => (
            <TakeCard key={take.id} take={take} name={exerciseName(take.exerciseId)}>
              <Button
                className="px-3 py-1.5"
                disabled={submit.isPending}
                onClick={(e) => {
                  e.preventDefault();
                  submit.mutate(take.id);
                }}
              >
                {t('takes.submit', 'Submit')}
              </Button>
              <Button
                variant="danger"
                className="px-3 py-1.5"
                disabled={remove.isPending}
                onClick={(e) => {
                  e.preventDefault();
                  remove.mutate(take.id);
                }}
              >
                {t('takes.delete', 'Delete')}
              </Button>
            </TakeCard>
          ))}
        </section>
      )}

      {awaiting.length > 0 && (
        <section className="space-y-2">
          <SectionHeader label={t('takes.awaiting', 'Awaiting review')} />
          {awaiting.map((take) => (
            <TakeCard key={take.id} take={take} name={exerciseName(take.exerciseId)}>
              <Tag tone="amber">{t('takes.pending', 'pending')}</Tag>
            </TakeCard>
          ))}
        </section>
      )}

      {reviewed.length > 0 && (
        <section className="space-y-2">
          <SectionHeader label={t('takes.reviewed', 'Reviewed')} />
          {reviewed.map((take) => (
            <TakeCard key={take.id} take={take} name={exerciseName(take.exerciseId)}>
              {take.reaction && REACTION_EMOJI[take.reaction] && (
                <span className="text-lg" title={take.reaction}>
                  {REACTION_EMOJI[take.reaction]}
                </span>
              )}
              <Tag tone="green">{t('takes.reviewedTag', 'reviewed')}</Tag>
            </TakeCard>
          ))}
        </section>
      )}

      {archived.length > 0 && (
        <section className="space-y-2">
          <button
            onClick={() => setShowArchived((s) => !s)}
            className="text-xs font-medium tracking-wide text-slate-400 uppercase hover:text-slate-600"
          >
            {showArchived ? '▾' : '▸'} {t('takes.archived', 'Archived')} ({archived.length})
          </button>
          {showArchived &&
            archived.map((take) => (
              <TakeCard key={take.id} take={take} name={exerciseName(take.exerciseId)}>
                <Tag>{t('takes.archivedTag', 'archived')}</Tag>
              </TakeCard>
            ))}
        </section>
      )}

      <ErrorText error={error} />
    </div>
  );
}

function SectionHeader({ label, hint }: { label: string; hint?: string }) {
  return (
    <p className="pt-2 text-xs font-medium tracking-wide text-slate-400 uppercase">
      {label}
      {hint && <span className="ml-2 font-normal normal-case">· {hint}</span>}
    </p>
  );
}

function TakeCard({ take, name, children }: { take: TakeRow; name: string; children?: ReactNode }) {
  return (
    <Link to={`/app/takes/${take.id}`} className="block">
      <Card className={take.status === 'archived' ? 'opacity-60' : 'hover:border-brand-300'}>
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold">{name}</p>
            <p className="mt-0.5 text-xs text-slate-500">
              {fmtDateTime(take.submittedAt ?? take.createdAt)}
              {take.accuracyScore !== null && ` · score ${take.accuracyScore}`}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">{children}</div>
        </div>
      </Card>
    </Link>
  );
}
